/**
 * SQL Completion Provider for Monaco Editor
 * Optimized implementation with caching, fuzzy matching, and unified logic
 */

import {
  MatchStrategy,
  SQLContext,
  WordInfo,
  SQL_KEYWORDS,
  MULTI_WORD_KEYWORDS,
  HIGH_PRIORITY_KEYWORDS,
  KEYWORD_PATTERNS,
  RESERVED_KEYWORDS
} from '../interfaces/dev-tool/db-viewer.interface';

export class SqlCompletionProvider {
  private tableNames: string[];
  private schemaData: Map<string, string[]>;

  // Caches
  private aliasCache = new Map<string, string>();
  private lastParsedText = '';
  private contextCache = new Map<string, { context: SQLContext; timestamp: number }>();

  // Pre-computed data structures (preserve original case)
  private tableNameMap: Map<string, string>; // lowercase -> original case
  private schemaDataMap: Map<string, string[]>; // lowercase table -> original fields
  private sqlKeywordsSet: Set<string>;

  // Fuzzy matching configuration
  private readonly FUZZY_MATCH_THRESHOLD = 2; // Max edit distance for matches
  private readonly FUZZY_MIN_WORD_LENGTH = 3; // Minimum word length to enable fuzzy matching

  constructor(
    tableNames: string[],
    schemaData: Map<string, string[]>
  ) {
    this.tableNames = tableNames;
    this.schemaData = schemaData;

    // Pre-compute case-preserving mappings
    this.tableNameMap = new Map<string, string>();
    tableNames.forEach(t => {
      this.tableNameMap.set(t.toLowerCase(), t);
    });

    this.schemaDataMap = new Map<string, string[]>();
    schemaData.forEach((fields, table) => {
      this.schemaDataMap.set(table.toLowerCase(), fields);
    });

    this.sqlKeywordsSet = new Set(SQL_KEYWORDS.map(k => k.toUpperCase()));
  }

  // Only trigger on dot and operators, NOT on space/enter/tab
  triggerCharacters = ['.', ',', '(', ')', '=', '<', '>', '!', ';'];

  /**
   * Main completion provider method
   */
  provideCompletionItems(model: any, position: any, context: any): any {
    const monaco = (window as any).monaco;

    // Extract word at cursor
    const currentLine = model.getLineContent(position.lineNumber);
    const word = this.getWordAtPosition(currentLine, position.column);

    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn
    };

    // Get context text
    const fullTextBeforeCursor = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column
    });

    const linesToCheck = Math.max(1, position.lineNumber - 5);
    const contextText = model.getValueInRange({
      startLineNumber: linesToCheck,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column
    });

    const recentText = contextText.substring(Math.max(0, contextText.length - 300));

    // Detect context (with caching)
    const sqlContext = this.detectContext(recentText);

    // Extract aliases (with caching)
    this.updateAliasCache(fullTextBeforeCursor);

    // Generate suggestions based on context
    return {
      suggestions: this.generateSuggestions(word, range, sqlContext, monaco),
      incomplete: false
    };
  }

  /**
   * Generate all suggestions based on context
   */
  private generateSuggestions(
    word: WordInfo,
    range: any,
    context: SQLContext,
    monaco: any
  ): any[] {
    const suggestions: any[] = [];
    const wordLower = word.word.toLowerCase();

    // Priority 1: Fields after dot (table.field) - with fuzzy matching
    if (context.afterDot && context.dotTableOrAlias) {
      const tableName = this.aliasCache.get(context.dotTableOrAlias.toLowerCase()) ||
        context.dotTableOrAlias;
      const fields = this.schemaDataMap.get(tableName.toLowerCase()) || [];

      // Use FUZZY matching and sort by score
      const fieldMatches: Array<{ field: string; score: number }> = [];

      fields.forEach(field => {
        if (this.matches(field, word.word, MatchStrategy.FUZZY)) {
          const score = this.getFuzzyScore(field, word.word);
          fieldMatches.push({ field, score });
        }
      });

      // Sort by fuzzy score (best matches first)
      fieldMatches.sort((a, b) => a.score - b.score);

      fieldMatches.forEach(({ field, score }) => {
        suggestions.push(this.createSuggestion({
          label: field,
          kind: monaco.languages.CompletionItemKind.Field,
          documentation: `Field from ${context.dotTableOrAlias}`,
          insertText: field,
          sortText: score.toString().padStart(3, '0') + field.toLowerCase(),
          range
        }));
      });

      if (suggestions.length > 0) {
        return suggestions;
      }
    }

    // Priority 2: Tables after FROM/JOIN/AS (but not if typing keyword) - with fuzzy matching
    if ((context.afterFrom || context.afterJoin || context.afterAs) && !context.isTypingKeyword) {
      const filterWord = wordLower === 'as' ? '' : word.word;

      // Use FUZZY matching and sort by score
      const tableMatches: Array<{ name: string; lowerName: string; score: number }> = [];

      this.tableNameMap.forEach((originalName, lowerName) => {
        if (this.matches(originalName, filterWord, MatchStrategy.FUZZY)) {
          const score = this.getFuzzyScore(originalName, filterWord);
          tableMatches.push({ name: originalName, lowerName, score });
        }
      });

      // Sort by fuzzy score (best matches first)
      tableMatches.sort((a, b) => a.score - b.score);

      tableMatches.forEach(({ name, score }) => {
        suggestions.push(this.createSuggestion({
          label: name,
          kind: monaco.languages.CompletionItemKind.Class,
          documentation: `Table: ${this.cleanTableName(name)}`,
          insertText: name,
          sortText: score.toString().padStart(3, '0') + name.toLowerCase(),
          range
        }));
      });
    }

    // Priority 3: Fields and aliases in WHERE/ORDER BY/GROUP BY/HAVING/ON - with fuzzy matching
    if (context.inWhere || context.inOrderBy || context.inGroupBy || context.inHaving || context.afterOn) {
      // Add table name suggestions (original case from schema) - with fuzzy matching
      const tableMatches: Array<{ name: string; aliasKey: string; score: number }> = [];

      this.aliasCache.forEach((originalTableName, aliasKey) => {
        if (this.matches(originalTableName, word.word, MatchStrategy.FUZZY)) {
          const score = this.getFuzzyScore(originalTableName, word.word);
          tableMatches.push({ name: originalTableName, aliasKey, score });
        }
      });

      // Sort by fuzzy score
      tableMatches.sort((a, b) => a.score - b.score);

      tableMatches.forEach(({ name, aliasKey, score }) => {
        suggestions.push(this.createSuggestion({
          label: name,
          kind: monaco.languages.CompletionItemKind.Variable,
          documentation: `Table: ${name}`,
          insertText: name,
          sortText: score.toString().padStart(3, '0') + aliasKey,
          range
        }));
      });

      // Add all fields (deduplicated, preserve original case, with fuzzy matching)
      const allFields = new Set<string>();
      const fieldMatches: Array<{ field: string; score: number }> = [];

      this.aliasCache.forEach((tableName) => {
        const fields = this.schemaDataMap.get(tableName.toLowerCase()) || [];
        fields.forEach(field => {
          const fieldLower = field.toLowerCase();
          if (!allFields.has(fieldLower) && this.matches(field, word.word, MatchStrategy.FUZZY)) {
            allFields.add(fieldLower);
            const score = this.getFuzzyScore(field, word.word);
            fieldMatches.push({ field, score });
          }
        });
      });

      // Sort by fuzzy score
      fieldMatches.sort((a, b) => a.score - b.score);

      fieldMatches.forEach(({ field, score }) => {
        suggestions.push(this.createSuggestion({
          label: field,
          kind: monaco.languages.CompletionItemKind.Field,
          documentation: `Field: ${field}`,
          insertText: field,
          sortText: (100 + score).toString().padStart(3, '0') + field.toLowerCase(),
          range
        }));
      });
    }

    // Priority 4: Keywords (only show when typing, not on trigger characters)
    const shouldShowKeywords = word.word.length >= 1;

    if (shouldShowKeywords) {
      SQL_KEYWORDS.forEach(keyword => {
        const keywordLower = keyword.toLowerCase();

        // Match: 1 char = prefix only, 2+ chars = prefix or contains
        const matches = word.word.length === 1
          ? keywordLower.startsWith(wordLower)
          : (keywordLower.startsWith(wordLower) || keywordLower.includes(wordLower));

        if (matches) {
          // Handle multi-word keywords
          let insertText: any = keyword;
          let sortPriority = '9';

          if (MULTI_WORD_KEYWORDS[keyword]) {
            if (keyword.toLowerCase().startsWith(wordLower)) {
              insertText = MULTI_WORD_KEYWORDS[keyword];
              sortPriority = '0';
            }
          } else if (HIGH_PRIORITY_KEYWORDS.has(keyword) && keywordLower.startsWith(wordLower)) {
            sortPriority = '0';
          }

          suggestions.push(this.createSuggestion({
            label: keyword,
            kind: monaco.languages.CompletionItemKind.Keyword,
            documentation: `SQL keyword: ${keyword}`,
            insertText,
            sortText: sortPriority + keywordLower,
            preselect: keywordLower.startsWith(wordLower),
            range
          }));
        }
      });
    }

    return suggestions;
  }

  /**
   * Detect SQL context from recent text (with caching)
   */
  private detectContext(text: string): SQLContext {
    const hash = this.simpleHash(text);
    const cached = this.contextCache.get(hash);

    if (cached && (Date.now() - cached.timestamp) < 100) {
      return cached.context;
    }

    const textTrimmed = text.trim();
    const textUpper = text.toUpperCase();

    // Detect dot notation
    const lastDotIndex = text.lastIndexOf('.');
    const afterDot = lastDotIndex > 0 && lastDotIndex >= text.length - 20;
    let dotTableOrAlias: string | undefined;

    if (afterDot) {
      const textBeforeDot = text.substring(Math.max(0, lastDotIndex - 50), lastDotIndex).trim();
      const tableMatch = textBeforeDot.match(/(\w+)\s*$/);
      if (tableMatch) {
        dotTableOrAlias = tableMatch[1].toLowerCase();
      }
    }

    // Detect keyword contexts
    const afterFrom = /\bFROM\s+\w*\s*$/i.test(textTrimmed) || /\bFROM\s+AS\s*$/i.test(textTrimmed);
    const afterJoin = /\b(INNER|LEFT|RIGHT|FULL|OUTER|CROSS)?\s*JOIN\s+\w*\s*$/i.test(textTrimmed) ||
      /\b(INNER|LEFT|RIGHT|FULL|OUTER|CROSS)?\s*JOIN\s*$/i.test(textTrimmed);
    const afterAs = /\bAS\s+\w*\s*$/i.test(textTrimmed) || /\bAS\s*$/i.test(textTrimmed);
    const afterOn = /\bON\s+\w*\s*$/i.test(textTrimmed) || /\bON\s*$/i.test(textTrimmed);

    const inWhere = textUpper.includes('WHERE') && !textUpper.endsWith('WHERE');
    const inOrderBy = textUpper.includes('ORDER BY') && !textUpper.endsWith('ORDER BY');
    const inGroupBy = textUpper.includes('GROUP BY') && !textUpper.endsWith('GROUP BY');
    const inHaving = textUpper.includes('HAVING') && !textUpper.endsWith('HAVING');

    const lastWord = textTrimmed.match(/\b(\w+)\s*$/);
    const isTypingKeyword = lastWord ? KEYWORD_PATTERNS.has(lastWord[1].toLowerCase()) : false;

    const keywordMatch = textTrimmed.match(/\b(SELECT|FROM|WHERE|JOIN|GROUP BY|ORDER BY|HAVING)\b\s*$/i);
    const lastKeyword = keywordMatch ? keywordMatch[1].toUpperCase() : undefined;

    const context: SQLContext = {
      afterDot,
      dotTableOrAlias,
      afterFrom,
      afterJoin,
      afterAs,
      afterOn,
      inWhere,
      inOrderBy,
      inGroupBy,
      inHaving,
      isTypingKeyword,
      lastKeyword
    };

    this.contextCache.set(hash, { context, timestamp: Date.now() });

    // Cleanup cache
    if (this.contextCache.size > 10) {
      const firstKey: any = this.contextCache.keys().next().value;
      this.contextCache.delete(firstKey);
    }

    return context;
  }

  /**
   * Update alias cache if needed
   */
  private updateAliasCache(fullText: string): void {
    // Quick check: only reparse if FROM/JOIN count changed
    if (this.shouldReparseAliases(fullText)) {
      this.extractAliases(fullText);
      this.lastParsedText = fullText;
    }
  }

  /**
   * Check if we need to reparse aliases
   */
  private shouldReparseAliases(text: string): boolean {
    if (this.lastParsedText === '') return true;

    if (Math.abs(text.length - this.lastParsedText.length) > 10) {
      return true;
    }

    const currentCount = (text.match(/\b(FROM|JOIN)\b/gi) || []).length;
    const cachedCount = (this.lastParsedText.match(/\b(FROM|JOIN)\b/gi) || []).length;

    return currentCount !== cachedCount;
  }

  /**
   * Extract table aliases from SQL text
   */
  private extractAliases(text: string): void {
    this.aliasCache.clear();

    // FROM pattern
    const fromPattern = /FROM\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi;
    this.processAliasMatches(text, fromPattern);

    // JOIN pattern
    const joinPattern = /(?:INNER|LEFT|RIGHT|FULL|CROSS)?\s+JOIN\s+(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi;
    this.processAliasMatches(text, joinPattern);
  }

  /**
   * Process regex matches to extract aliases
   * Preserves original table name case from schema
   */
  private processAliasMatches(text: string, pattern: RegExp): void {
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      const tableNameFromQuery = match[1]; // Keep original case from query
      let alias = match[2]?.toLowerCase();

      // Check for implicit alias (next word after table name)
      if (!alias) {
        const afterMatch = text.substring(match.index + match[0].length).trim();
        const nextWordMatch = afterMatch.match(/^(\w+)/);
        if (nextWordMatch) {
          const nextWord = nextWordMatch[1].toLowerCase();
          if (!RESERVED_KEYWORDS.has(nextWord) && nextWord !== 'on') {
            alias = nextWord;
          }
        }
      }

      // Look up the original case table name from schema
      const originalTableName = this.tableNameMap.get(tableNameFromQuery.toLowerCase()) || tableNameFromQuery;

      // Map: alias (lowercase) → original case table name
      if (alias && !RESERVED_KEYWORDS.has(alias)) {
        this.aliasCache.set(alias, originalTableName);
      }

      // Map: table name (lowercase) → original case table name
      // This allows using table name without alias in WHERE/GROUP BY
      this.aliasCache.set(tableNameFromQuery.toLowerCase(), originalTableName);
    }
  }

  /**
   * Extract word at cursor position
   */
  private getWordAtPosition(line: string, column1Based: number): WordInfo {
    const idx = Math.max(0, Math.min(line.length, column1Based - 1));
    const wordCharPattern = /[A-Za-z0-9_]/;

    let start = idx;
    let end = idx;

    while (start > 0 && wordCharPattern.test(line[start - 1])) start--;
    while (end < line.length && wordCharPattern.test(line[end])) end++;

    return {
      word: line.substring(start, end),
      startColumn: start + 1,
      endColumn: end + 1
    };
  }

  /**
   * Match item against word using strategy
   * Enhanced with fuzzy matching for typo tolerance
   */
  private matches(item: string, word: string, strategy: MatchStrategy): boolean {
    if (!word) return true;

    const itemLower = item.toLowerCase();
    const wordLower = word.toLowerCase();

    switch (strategy) {
      case MatchStrategy.PREFIX:
        return itemLower.startsWith(wordLower);

      case MatchStrategy.CONTAINS:
        return itemLower.includes(wordLower);

      case MatchStrategy.FUZZY:
        // Fuzzy matching: handle typos using Levenshtein distance

        // Quick exact match check first (performance optimization)
        if (itemLower.includes(wordLower)) return true;

        // Only apply fuzzy matching for words of minimum length
        if (wordLower.length < this.FUZZY_MIN_WORD_LENGTH) {
          return itemLower.includes(wordLower); // Fall back to contains
        }

        // Check if word is close enough to item (full match)
        if (this.levenshteinDistance(wordLower, itemLower) <= this.FUZZY_MATCH_THRESHOLD) {
          return true;
        }

        // Check if word is close to start of item (prefix fuzzy match)
        const itemPrefix = itemLower.substring(0, wordLower.length);
        if (this.levenshteinDistance(wordLower, itemPrefix) <= this.FUZZY_MATCH_THRESHOLD) {
          return true;
        }

        // Check if word matches any substring of item (sliding window)
        // Only for reasonable-length items to avoid performance issues
        if (itemLower.length <= 30) {
          for (let i = 0; i <= itemLower.length - wordLower.length; i++) {
            const substring = itemLower.substring(i, i + wordLower.length);
            if (this.levenshteinDistance(wordLower, substring) <= this.FUZZY_MATCH_THRESHOLD) {
              return true;
            }
          }
        }

        return false;

      default:
        return itemLower.includes(wordLower);
    }
  }

  /**
   * Calculate Levenshtein distance between two strings
   * Used for fuzzy matching to handle typos
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const len1 = str1.length;
    const len2 = str2.length;

    // Quick exits for performance
    if (len1 === 0) return len2;
    if (len2 === 0) return len1;
    if (str1 === str2) return 0;

    // Create distance matrix
    const matrix: number[][] = Array(len1 + 1)
      .fill(null)
      .map(() => Array(len2 + 1).fill(0));

    // Initialize first column and row
    for (let i = 0; i <= len1; i++) matrix[i][0] = i;
    for (let j = 0; j <= len2; j++) matrix[0][j] = j;

    // Calculate distances
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,      // deletion
          matrix[i][j - 1] + 1,      // insertion
          matrix[i - 1][j - 1] + cost // substitution
        );
      }
    }

    return matrix[len1][len2];
  }

  /**
   * Calculate fuzzy match score for sorting
   * Lower score = better match
   */
  private getFuzzyScore(item: string, word: string): number {
    if (!word) return 0;

    const itemLower = item.toLowerCase();
    const wordLower = word.toLowerCase();

    // Exact match - highest priority
    if (itemLower === wordLower) return 0;

    // Prefix match - very high priority
    if (itemLower.startsWith(wordLower)) return 1;

    // Contains match - high priority
    if (itemLower.includes(wordLower)) return 2;

    // Fuzzy match - calculate distance
    const fullDistance = this.levenshteinDistance(wordLower, itemLower);
    const prefixDistance = this.levenshteinDistance(
      wordLower,
      itemLower.substring(0, wordLower.length)
    );

    // Return minimum distance + base score
    return 10 + Math.min(fullDistance, prefixDistance);
  }

  /**
   * Create suggestion item
   */
  private createSuggestion(config: {
    label: string;
    kind: any;
    documentation?: string;
    insertText?: string;
    sortText: string;
    preselect?: boolean;
    range: any;
  }): any {
    return {
      label: config.label,
      kind: config.kind,
      documentation: config.documentation || '',
      insertText: config.insertText || config.label,
      range: config.range,
      sortText: config.sortText,
      preselect: config.preselect || false
    };
  }

  /**
   * Simple hash for caching
   */
  private simpleHash(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  /**
   * Clean table name for display
   */
  private cleanTableName(tableName: string): string {
    return tableName
      .replace(/^t_/i, '')
      .replace(/^tbl_/i, '')
      .replace(/^tab_/i, '')
      .replace(/^TABD_/i, '')
      .replace(/^TABMD_/i, '');
  }
}